/**
 * Поля формы кабинета.
 *
 * Правила app-kit.md §2, и они не про красоту:
 *
 * * **подпись видна всегда** — placeholder подписью не бывает: он исчезает от
 *   первого знака, и человек, вернувшийся к наполовину заполненной форме, не
 *   знает, что в поле;
 * * **подсказка не двигается**, когда появляется ошибка, — ошибка встаёт на её
 *   место, поэтому от неверного ввода не прыгает вся форма;
 * * ошибка — это `aria-invalid` и текст, привязанный `aria-describedby`, а не
 *   красная рамка: цвет не единственный признак нигде в системе.
 *
 * Раньше поле жило внутри `routes/auth/AuthCard.tsx`. Форм в кабинете стало
 * больше, чем форм входа, и держать общий контрол в папке одного экрана значит
 * рано или поздно получить его вторую копию.
 */

import type { ReactNode } from 'react';

export interface FieldRenderProps {
  id: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string;
}

export function Field({
  id,
  label,
  hint,
  error,
  optional = false,
  children,
}: {
  id: string;
  label: string;
  /** Одна строка под полем. Занимает место всегда, даже пустая. */
  hint: string;
  error?: string;
  /**
   * Помечается то, чего меньше (app-kit.md §2). В формах кабинета обязательно
   * почти всё, поэтому звёздочек у обязательных полей нет — вместо них подпись
   * «необязательно» у редкого исключения.
   */
  optional?: boolean;
  children: (props: FieldRenderProps) => ReactNode;
}) {
  const describedBy = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional ? <span className="field-optional">необязательно</span> : null}
      </label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })}
      <span id={describedBy} className={`hint${error ? ' error' : ''}`}>
        {error ?? hint}
      </span>
    </div>
  );
}

/**
 * Список «подпись — значение»: подпись слева, значение справа, одна строка на
 * пару. Им набраны сводка подписки, реквизиты и карточка клиента: это не
 * таблица — сравнивать тут нечего, а колонка из двух ячеек на узком экране
 * разъезжается.
 *
 * Назывался `.facts` и лоб в лоб сталкивался со сводкой показателей «Обзора
 * бренда», у которой то же имя и несовместимая раскладка: `account.css`
 * подключён после `screens.css`, поэтому его `display:flex` побеждал, и в
 * ячейке шириной 285 px в одну строку выстраивались подпись, число, пилюля и
 * длинное основание. Совпавшие имена дизайн-система требует разводить
 * (components.md, таблица переименований; defects.md часть 4), а не разбирать
 * порядком подключения файлов.
 */
export function Kv({ children }: { children: ReactNode }) {
  return <dl className="kv">{children}</dl>;
}

export function KvRow({
  label,
  children,
  note,
}: {
  label: string;
  children: ReactNode;
  /** Вторая строка мелким: основание, срок, пояснение к значению. */
  note?: ReactNode;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {children}
        {note ? <span className="stamp">{note}</span> : null}
      </dd>
    </div>
  );
}

/**
 * Раздел экрана: заголовок, одно предложение назначения, содержимое.
 *
 * Экран настроек без такого разделения превращается в простыню полей, в которой
 * оплата стоит рядом с часовым поясом.
 */
export function Panel({
  title,
  lead,
  action,
  children,
}: {
  title: string;
  lead?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          {lead ? <p>{lead}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
